import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Switch, Checkbox, Space, Button, InputNumber, Typography, Tooltip, Alert } from 'antd';
import { PlusOutlined, MinusCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import AceEditor from 'react-ace';
import YAML from 'yaml';
import 'ace-builds/webpack-resolver';
import 'ace-builds/src-noconflict/mode-yaml';
import 'ace-builds/src-noconflict/theme-github';
import styles from './PerFieldProvideField.module.scss';
const isProvideObject = (value) => {
    return typeof value === 'object' && value !== null && '##provide' in value;
};
const isLiteralObject = (value) => {
    return typeof value === 'object' && value !== null && '##literal' in value;
};
const isUnstructuredType = (schema) => {
    // Untyped field, likely "any"
    if (!schema.type && !schema.oneOf && !schema.anyOf && !schema.allOf && !schema.properties && !schema.items) {
        return true;
    }
    // Union type
    if (Array.isArray(schema.type) && schema.type.length > 1) {
        return true;
    }
    // Generic object
    if (schema.type === 'object' && !schema.properties) {
        return true;
    }
    // Generic array
    if (schema.type === 'array' && !schema.items) {
        return true;
    }
    return false;
};
const PerFieldProvideField = (props) => {
    const { t } = useTranslation();
    const { schema, name, formData, onChange, uiSchema, registry, required } = props;
    const initialMode = isProvideObject(formData) ? 'provide' : 'value';
    const [mode, setMode] = useState(initialMode);
    const [yamlError, setYamlError] = useState(null);
    const [yamlText, setYamlText] = useState('');
    const [isYamlEdited, setIsYamlEdited] = useState(false);
    const handleModeChange = (newMode) => {
        setMode(newMode);
        if (newMode === 'provide') {
            onChange({ '##provide': {} });
        }
        else {
            if (yamlText.trim() !== '') {
                try {
                    const parsed = YAML.parse(yamlText);
                    onChange(parsed);
                }
                catch (_a) {
                    onChange(undefined);
                }
            }
            else {
                onChange(undefined);
            }
        }
        setIsYamlEdited(false);
    };
    const handleValueChange = (e) => {
        onChange(e.target.value);
    };
    const handleLiteralChange = (e) => {
        onChange({ '##literal': e.target.value });
    };
    const handleCheckboxChange = (e) => {
        if (e.target.checked) {
            onChange({ '##literal': formData || '' });
        }
        else {
            onChange((formData === null || formData === void 0 ? void 0 : formData['##literal']) || '');
        }
    };
    const handleProviderChange = (newProviderData) => {
        onChange({
            '##provide': newProviderData,
        });
    };
    const handleYamlChange = (newValue) => {
        if (!isYamlEdited) {
            setIsYamlEdited(true);
        }
        setYamlText(newValue);
        if (newValue.trim() === '') {
            onChange(undefined);
            setYamlError(null);
            return;
        }
        try {
            const parsed = YAML.parse(newValue);
            onChange(parsed);
            setYamlError(null);
        }
        catch (e) {
            onChange(undefined);
            setYamlError(t('invalidYamlFormat', 'Invalid YAML format'));
        }
    };
    const getYamlValue = (value) => {
        if (value === undefined || value === null) {
            return '';
        }
        try {
            return YAML.stringify(value);
        }
        catch (e) {
            try {
                return JSON.stringify(value, null, 2);
            }
            catch (jsonError) {
                return '';
            }
        }
    };
    useEffect(() => {
        if (!isYamlEdited && !isProvideObject(formData)) {
            setYamlText(getYamlValue(formData));
        }
    }, [formData, isYamlEdited]);
    useEffect(() => {
        const newMode = isProvideObject(formData) ? 'provide' : 'value';
        if (newMode !== mode) {
            setMode(newMode);
        }
    }, [formData]);
    const renderValueInput = () => {
        if (schema.type === 'boolean') {
            const { checkedChildren, unCheckedChildren } = (uiSchema === null || uiSchema === void 0 ? void 0 : uiSchema['ui:options']) || {};
            return (React.createElement(Switch, { checked: formData, onChange: (checked) => onChange(checked), checkedChildren: checkedChildren || t('switchOn', 'On'), unCheckedChildren: unCheckedChildren || t('switchOff', 'Off') }));
        }
        const { SchemaField } = registry.fields;
        const newProps = Object.assign({}, props);
        // Remove the custom ui:field to avoid infinite recursion
        if (newProps.uiSchema) {
            const newUiSchema = Object.assign({}, newProps.uiSchema);
            delete newUiSchema['ui:field'];
            newProps.uiSchema = newUiSchema;
        }
        if (schema.enum) {
            const isLiteral = isLiteralObject(formData);
            const isProvide = isProvideObject(formData);
            let value = formData;
            if (isLiteral) {
                value = formData['##literal'];
            }
            else if (isProvide || (typeof formData === 'object' && formData !== null && !isLiteral)) {
                value = undefined;
            }
            return (React.createElement(Space, { direction: "vertical", style: { width: '100%' } },
                React.createElement(Select, { value: value, onChange: (val) => {
                        if (isLiteral) {
                            onChange({ '##literal': val });
                        }
                        else {
                            onChange(val);
                        }
                    }, placeholder: t('selectValuePlaceholder', 'Select a value'), allowClear: true }, schema.enum.map((option) => (React.createElement(Select.Option, { key: option, value: option }, option)))),
                React.createElement(Checkbox, { checked: isLiteral, onChange: handleCheckboxChange, className: styles.valueInput },
                    React.createElement(Trans, { i18nKey: "doNotEvaluate" }),
                    React.createElement(Tooltip, { title: React.createElement(Trans, { i18nKey: "doNotEvaluateTooltip" }) },
                        React.createElement(InfoCircleOutlined, { className: styles.evaluateTooltip })))));
        }
        if (schema.type === 'string') {
            const isLiteral = isLiteralObject(formData);
            const value = isLiteral ? formData['##literal'] : formData;
            return (React.createElement(Space, { direction: "vertical", style: { width: '100%' } },
                React.createElement(Input.TextArea, { value: value, onChange: isLiteral ? handleLiteralChange : handleValueChange, autoSize: { minRows: 1, maxRows: 10 } }),
                React.createElement(Checkbox, { checked: isLiteral, onChange: handleCheckboxChange, className: styles.valueInput },
                    React.createElement(Trans, { i18nKey: "doNotEvaluate" }),
                    React.createElement(Tooltip, { title: React.createElement(Trans, { i18nKey: "doNotEvaluateTooltip" }) },
                        React.createElement(InfoCircleOutlined, { className: styles.evaluateTooltip })))));
        }
        if (schema.type === 'number' || schema.type === 'integer') {
            const isLiteral = isLiteralObject(formData);
            const rawValue = isLiteral ? formData['##literal'] : formData;
            const value = typeof rawValue === 'number' ? rawValue : undefined;
            const step = schema.type === 'integer' ? 1 : undefined;
            const precision = schema.type === 'integer' ? 0 : undefined;
            const handleNumberChange = (val) => {
                if (val === null || val === undefined) {
                    onChange(isLiteral ? { '##literal': undefined } : undefined);
                    return;
                }
                onChange(isLiteral ? { '##literal': val } : val);
            };
            return (React.createElement(Space, { direction: "vertical", style: { width: '100%' } },
                React.createElement(InputNumber, { value: value, onChange: handleNumberChange, step: step, precision: precision, style: { width: '100%' } }),
                React.createElement(Checkbox, { checked: isLiteral, onChange: handleCheckboxChange, className: styles.valueInput },
                    React.createElement(Trans, { i18nKey: "doNotEvaluate" }),
                    React.createElement(Tooltip, { title: React.createElement(Trans, { i18nKey: "doNotEvaluateTooltip" }) },
                        React.createElement(InfoCircleOutlined, { className: styles.evaluateTooltip })))));
        }
        if (isUnstructuredType(schema)) {
            return (React.createElement(Space, { direction: "vertical", style: { width: '100%' } },
                React.createElement(Typography.Text, { type: "secondary", style: { fontSize: '12px' } }, t('enterYamlFormatFor', 'Enter YAML format for {{type}} data:', {
                    type: Array.isArray(schema.type) ? schema.type.join(', ') : schema.type,
                })),
                React.createElement(Alert, { message: React.createElement(Trans, { i18nKey: "preventEvaluationTitle", defaults: "How to prevent evaluation for specific values" }), description: React.createElement("div", null,
                        React.createElement("p", null,
                            React.createElement(Trans, { i18nKey: "preventEvaluationDescription", defaults: "To prevent evaluation of a string value that looks like a token, use the `##literal` syntax. This is useful for values that should be treated as plain text." })),
                        React.createElement("p", null,
                            React.createElement("strong", null,
                                React.createElement(Trans, { i18nKey: "exampleLabel", defaults: "Example:" }))),
                        React.createElement("pre", { style: {
                                background: '#f5f5f5',
                                padding: '10px',
                                borderRadius: '4px',
                                marginTop: '10px',
                                whiteSpace: 'pre-wrap',
                            } }, `my_key:
  '##literal': 'your-value-to-not-evaluate'`)), type: "info", showIcon: true, style: { marginTop: '8px', marginBottom: '8px' } }),
                React.createElement(AceEditor, { mode: "yaml", theme: "github", value: yamlText, onChange: handleYamlChange, name: `yaml-editor-${name}`, width: "100%", height: "150px", fontSize: 14, editorProps: { $blockScrolling: true }, setOptions: {
                        useWorker: false,
                    } }),
                yamlError && (React.createElement(Alert, { message: yamlError, type: "error", showIcon: true, style: { marginTop: '8px' } }))));
        }
        return React.createElement(SchemaField, Object.assign({}, newProps));
    };
    const renderProvideInput = () => {
        var _a;
        const provideData = (formData === null || formData === void 0 ? void 0 : formData['##provide']) || {};
        const availableProvidersRaw = ((_a = uiSchema === null || uiSchema === void 0 ? void 0 : uiSchema['ui:options']) === null || _a === void 0 ? void 0 : _a.availableProviders) || [];
        const availableProviders = Array.isArray(availableProvidersRaw) ? availableProvidersRaw : [];
        const keyArgs = Array.isArray(provideData.keyArgs) ? provideData.keyArgs : [];
        const handleKeyArgsChange = (newKeyArgs) => {
            handleProviderChange(Object.assign(Object.assign({}, provideData), { keyArgs: newKeyArgs }));
        };
        const handleArgChange = (index, newValue) => {
            const newKeyArgs = [...keyArgs];
            newKeyArgs[index] = newValue;
            handleKeyArgsChange(newKeyArgs);
        };
        const handleArgTypeChange = (index, newType) => {
            const newKeyArgs = [...keyArgs];
            if (newType === 'string')
                newKeyArgs[index] = '';
            if (newType === 'number')
                newKeyArgs[index] = 0;
            if (newType === 'boolean')
                newKeyArgs[index] = false;
            handleKeyArgsChange(newKeyArgs);
        };
        const addArg = () => {
            handleKeyArgsChange([...keyArgs, '']);
        };
        const removeArg = (index) => {
            const newKeyArgs = [...keyArgs];
            newKeyArgs.splice(index, 1);
            handleKeyArgsChange(newKeyArgs);
        };
        return (React.createElement(Space, { direction: "vertical", style: { width: '100%' } },
            React.createElement(Form.Item, { label: React.createElement(Trans, { i18nKey: "provider", defaults: "Provider" }), style: { marginBottom: 0 } },
                React.createElement(Select, { value: provideData.provider, onChange: (provider) => handleProviderChange(Object.assign(Object.assign({}, provideData), { provider })), placeholder: t('selectProviderPlaceholder', 'Select a provider') }, availableProviders.map((p) => React.createElement(Select.Option, { key: p, value: p }, p)))),
            React.createElement(Form.Item, { label: React.createElement(Trans, { i18nKey: "keyMethod", defaults: "Key/Method" }), style: { marginBottom: 0 } },
                React.createElement(Input, { value: provideData.key, onChange: (e) => handleProviderChange(Object.assign(Object.assign({}, provideData), { key: e.target.value })) })),
            React.createElement(Form.Item, { label: React.createElement(Trans, { i18nKey: "argumentsLabel", defaults: "Arguments" }), style: { marginBottom: 0 } },
                React.createElement("div", { style: { border: '1px solid #d9d9d9', padding: '8px', borderRadius: '2px' } },
                    keyArgs.map((arg, index) => {
                        const argType = typeof arg;
                        const handleTypeChange = (newType) => handleArgTypeChange(index, newType);
                        const handleValueChange = (val) => handleArgChange(index, val);
                        return (React.createElement(Space, { key: index, style: { display: 'flex', marginBottom: 8 }, align: "baseline" },
                            React.createElement(Select, { value: argType, onChange: handleTypeChange, style: { width: 100 } },
                                React.createElement(Select.Option, { value: "string" },
                                    React.createElement(Trans, { i18nKey: "typeString", defaults: "String" })),
                                React.createElement(Select.Option, { value: "number" },
                                    React.createElement(Trans, { i18nKey: "typeNumber", defaults: "Number" })),
                                React.createElement(Select.Option, { value: "boolean" },
                                    React.createElement(Trans, { i18nKey: "typeBoolean", defaults: "Boolean" }))),
                            argType === 'string' && React.createElement(Input, { value: arg, onChange: (e) => handleValueChange(e.target.value) }),
                            argType === 'number' && React.createElement(InputNumber, { value: arg, onChange: handleValueChange }),
                            argType === 'boolean' && React.createElement(Checkbox, { checked: arg, onChange: (e) => handleValueChange(e.target.checked) }),
                            React.createElement(MinusCircleOutlined, { onClick: () => removeArg(index) })));
                    }),
                    React.createElement(Button, { type: "dashed", onClick: addArg, block: true, icon: React.createElement(PlusOutlined, null) },
                        React.createElement(Trans, { i18nKey: "addArgument", defaults: "Add Argument" }))))));
    };
    return (React.createElement("div", { className: styles.fieldContainer },
        React.createElement("div", { className: styles.switchContainer },
            React.createElement(Typography.Text, { className: styles.switchLabel },
                schema.title || name,
                required && React.createElement("span", { className: styles.requiredIndicator }, "*")),
            React.createElement(Switch, { checkedChildren: t('provideModeLabel', 'provide'), unCheckedChildren: t('staticValueModeLabel', 'static value'), checked: mode === 'provide', onChange: (checked) => handleModeChange(checked ? 'provide' : 'value') })),
        mode === 'value' ? renderValueInput() : renderProvideInput()));
};
export default PerFieldProvideField;
